import { Justification } from '@schooly/api';
import { AttendanceCodeCircle } from '@schooly/components/attendance';
import { AttendanceCodeSelectOption } from '@schooly/components/filters';
import { MD_BREAKPOINT_TABLE_MIN_WIDTH } from '@schooly/constants';
import { Grid, GridBody, GridContainer } from '@schooly/style';
import { FC, useMemo } from 'react';

import { useAttendanceNotificationsEnabled } from '../../../hooks/useAttendanceNotificationsEnabled';
import { hasNotification } from '../attendance-utils';
import { AttendanceJustificationsTableHeader } from './AttendanceJustificationsTableHeader';
import {
  AttendanceJustificationsTableRow,
  AttendanceJustificationsTableRowProps,
} from './AttendanceJustificationsTableRow';

const SELECT_CIRCLE_SIZE = 20;

type AttendanceJustificationsTableProps = {
  justifications: Justification[];
  date: string;
} & Omit<AttendanceJustificationsTableRowProps, 'justification' | 'codeOptions'>;

export const AttendanceJustificationsTable: FC<AttendanceJustificationsTableProps> = ({
  justifications,
  date,
  codes,
  ...props
}) => {
  const notificationsEnabled = useAttendanceNotificationsEnabled();

  const codeOptions = useMemo(() => {
    return codes.reduce<AttendanceCodeSelectOption[][]>(
      (acc, code) => {
        acc[code.is_present ? 0 : 1].push({
          label: code.name,
          id: code.id,
          withNotification: hasNotification(code, notificationsEnabled),
          avatar: <AttendanceCodeCircle code={code} size={SELECT_CIRCLE_SIZE} />,
        });
        return acc;
      },
      [[], []],
    );
  }, [codes, notificationsEnabled]);

  return (
    <GridContainer
      sx={(theme) => ({
        [theme.breakpoints.down('md')]: { minWidth: MD_BREAKPOINT_TABLE_MIN_WIDTH },
      })}
    >
      <Grid fixedLayout>
        <AttendanceJustificationsTableHeader date={date} />

        <GridBody>
          {justifications.map((justification) => (
            <AttendanceJustificationsTableRow
              key={justification.id}
              justification={justification}
              codes={codes}
              codeOptions={codeOptions}
              {...props}
            />
          ))}
        </GridBody>
      </Grid>
    </GridContainer>
  );
};
